import { DocumentIcon } from '@/shared/assets'
import { CommandGroup, CommandItem } from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { cn } from '@/true-fsd/shared/utils/css'
import { ContractUnion } from '@synapex/shared2/gateway-rest-api'
import { PlusIcon } from 'lucide-react'

const commandItemClassName =
    'flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light'

const contractItemClassName =
    'flex cursor-pointer gap-2 rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light'

type CreateContractItemProps = {
    type: 'default' | 'custom'
    label: string
    canCreate: boolean
    tooltip?: string
    onSelect: (type: 'default' | 'custom') => void
}

function CreateContractItem({
    type,
    label,
    canCreate,
    tooltip,
    onSelect,
}: CreateContractItemProps) {
    return (
        <CommandItem
            onSelect={() => onSelect(type)}
            className={cn(
                commandItemClassName,
                !canCreate && 'cursor-not-allowed text-synapex-light-gray'
            )}
        >
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="flex w-full items-center justify-between">
                        <span>{label}</span>
                        <PlusIcon className="h-3 w-3" />
                    </div>
                </TooltipTrigger>
                {!canCreate && tooltip && (
                    <TooltipContent side="right">
                        <span>{tooltip}</span>
                    </TooltipContent>
                )}
            </Tooltip>
        </CommandItem>
    )
}

type ContractSelectionListProps = {
    contracts: ContractUnion[] | undefined
    canCreateContract: boolean
    createContractTooltip?: string
    createNewDocumentMessage: string
    onCreateContract: (type: 'default' | 'custom') => void
    onSelectDefaultContract: (value: string) => void
    onSelectCustomContract: (customContractId: number) => void
}

export function ContractSelectionList({
    contracts,
    canCreateContract,
    createContractTooltip,
    createNewDocumentMessage,
    onCreateContract,
    onSelectDefaultContract,
    onSelectCustomContract,
}: ContractSelectionListProps) {
    return (
        <CommandGroup className="p-0">
            <CreateContractItem
                type="default"
                label={createNewDocumentMessage}
                canCreate={canCreateContract}
                tooltip={createContractTooltip}
                onSelect={onCreateContract}
            />
            <CreateContractItem
                type="custom"
                label="Ввести свой № договора"
                canCreate={canCreateContract}
                tooltip={createContractTooltip}
                onSelect={onCreateContract}
            />
            {contracts?.map((contract) => {
                const isCustom = contract.type === 'custom'
                const key = isCustom
                    ? contract.custom_contract_id
                    : contract.documentRecordId
                const label = isCustom ? contract.number : contract.fileName

                return (
                    <CommandItem
                        key={key}
                        value={isCustom ? undefined : key.toString()}
                        onSelect={
                            isCustom
                                ? () =>
                                      onSelectCustomContract(
                                          contract.custom_contract_id
                                      )
                                : onSelectDefaultContract
                        }
                        disabled={!isCustom && contract.has_final}
                        className={contractItemClassName}
                    >
                        <img src={DocumentIcon} className="h-4 w-3" />
                        <span>{label}</span>
                    </CommandItem>
                )
            })}
        </CommandGroup>
    )
}
